/**
 * Authentication types and interfaces for the application
 * Provides type safety and clear contracts for auth-related operations
 */

export interface User {
  id: string;
  email: string | null;
  name: string;
  picture?: string;
  verified_email?: boolean;
  has_password?: boolean;
  email_verified?: boolean;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  // WE-115: true for a brand-new registration (backend created a trial
  // subscription for this user in this request), false for a returning
  // user. Defaults to false server-side if the backend hasn't deployed the
  // field yet, and undefined here purely for that same rollout gap.
  is_new_user?: boolean;
}

export interface AuthState {
  user: Partial<User> | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface GoogleAuthResponse {
  credential: string;
  select_by: string;
}

export interface AuthContextType extends AuthState {
  login: (googleToken: string, attribution?: AttributionPayload) => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  loginWithMagicLinkToken: (token: string, attribution?: AttributionPayload) => Promise<void>;
  loginWithAmazon: (accessToken: string, attribution?: AttributionPayload) => Promise<void>;
  loginWithTelegram: (data: TelegramAuthData, attribution?: AttributionPayload) => Promise<void>;
  sendMagicLink: (email: string, attribution?: AttributionPayload) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  // WE-115: see AuthTokens.is_new_user below — same field, this is the raw
  // shape returned by the HTTP layer before it's stored as AuthTokens.
  is_new_user?: boolean;
}

export interface AttributionPayload {
  utm_id?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  yclid?: string;
  msclkid?: string;
  ttclid?: string;
  twclid?: string;
  wiz_cid?: string;
  referrer?: string;
  landing_page?: string;
}

export interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export type TelegramAuthData = {
  id: number;
  first_name: string;
  last_name?: string | null;
  username?: string | null;
  photo_url?: string | null;
  auth_date: number;
  hash: string;
  attribution?: AttributionPayload;
};

export interface GoogleTokenPayload {
  iss: string;
  sub: string;
  email: string;
  email_verified: boolean;
  name: string;
  picture: string;
  given_name: string;
  family_name: string;
  iat: number;
  exp: number;
}

export interface AuthMessageResponse {
  message: string;
}

export enum AuthErrorType {
  INVALID_TOKEN = 'INVALID_TOKEN',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  GOOGLE_AUTH_ERROR = 'GOOGLE_AUTH_ERROR',
  BACKEND_AUTH_ERROR = 'BACKEND_AUTH_ERROR',
  REFRESH_TOKEN_EXPIRED = 'REFRESH_TOKEN_EXPIRED',
  UNAUTHORIZED = 'UNAUTHORIZED',
  AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR',
}

export class AuthError extends Error {
  constructor(
    public type: AuthErrorType,
    message: string,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Global type declarations for Google Identity Services
 */
declare global {
  interface Window {
    dataLayer?: Array<any>;
    gtag?: (...args: any[]) => void;
    amazon?: {
      Login: {
        setClientId: (clientId: string) => void;
        authorize: (
          options: { scope: string; scope_data?: Record<string, { essential: boolean }>; response_type?: string },
          callback: (response: { access_token?: string; error?: string; error_description?: string }) => void
        ) => void;
      };
    };
    TelegramLoginWidget?: {
      dataOnauth: (user: any) => void;
    };
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: any) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            element: HTMLElement | null,
            options: {
              type?: 'standard' | 'icon';
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              logo_alignment?: 'left' | 'center';
              width?: number;
              locale?: string;
            }
          ) => void;
          prompt: (callback?: (notification: any) => void) => void;
          revoke: (email: string, callback: (response: any) => void) => void;
          cancel: () => void;
        };
      };
    };
  }
}
